/**
 * Retry Service with Exponential Backoff
 *
 * Provides retry logic for API calls that fail transiently.
 * Configurable max retries, base delay, and exponential backoff factor.
 */

import { errorLoggingService } from './errorLoggingService';

export interface RetryConfig {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries: number;
  /** Base delay in milliseconds (default: 1000) */
  baseDelay: number;
  /** Backoff multiplier (default: 2) */
  backoffFactor: number;
  /** Maximum delay cap in milliseconds (default: 30000) */
  maxDelay: number;
  /** Whether to add jitter to prevent thundering herd (default: true) */
  jitter: boolean;
  /** HTTP status codes that should trigger a retry (default: [408, 429, 500, 502, 503, 504]) */
  retryableStatuses: number[];
  /** Callback invoked on each retry attempt */
  onRetry?: (attempt: number, error: Error, delay: number) => void;
}

const DEFAULT_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  backoffFactor: 2,
  maxDelay: 30000,
  jitter: true,
  retryableStatuses: [408, 429, 500, 502, 503, 504],
};

/**
 * Determine if an error is retryable
 */
export function isRetryableError(error: unknown, config: RetryConfig): boolean {
  // Network errors (no response) are retryable
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // Check for network-related error messages
  if (error instanceof Error) {
    const networkErrors = ['network', 'timeout', 'aborted', 'ECONNREFUSED', 'ECONNRESET'];
    if (networkErrors.some((msg) => error.message.toLowerCase().includes(msg.toLowerCase()))) {
      return true;
    }
  }

  // Check HTTP status codes
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    return config.retryableStatuses.includes(status);
  }

  return false;
}

/**
 * Calculate delay for a given attempt with optional jitter
 */
export function calculateDelay(attempt: number, config: RetryConfig): number {
  const exponentialDelay = config.baseDelay * Math.pow(config.backoffFactor, attempt);
  const cappedDelay = Math.min(exponentialDelay, config.maxDelay);

  if (config.jitter) {
    // Add random jitter between 0 and 50% of the delay
    const jitterAmount = cappedDelay * 0.5 * Math.random();
    return Math.floor(cappedDelay + jitterAmount);
  }

  return Math.floor(cappedDelay);
}

/**
 * Sleep for a specified duration
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Execute a function with retry logic and exponential backoff
 *
 * @param fn - The async function to execute
 * @param config - Partial retry configuration (merged with defaults)
 * @returns The result of the function
 * @throws The last error if all retries are exhausted
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  config?: Partial<RetryConfig>
): Promise<T> {
  const mergedConfig: RetryConfig = { ...DEFAULT_CONFIG, ...config };
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= mergedConfig.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // If this is the last attempt or error is not retryable, throw
      if (attempt === mergedConfig.maxRetries || !isRetryableError(error, mergedConfig)) {
        // Log the final failure
        errorLoggingService.logError({
          message: `Request failed after ${attempt + 1} attempt(s): ${lastError.message}`,
          stack: lastError.stack,
          action: 'api-retry-exhausted',
          metadata: {
            attempts: attempt + 1,
            maxRetries: mergedConfig.maxRetries,
          },
        });
        throw lastError;
      }

      // Calculate delay and wait
      const delay = calculateDelay(attempt, mergedConfig);

      // Log the retry attempt
      errorLoggingService.logWarning({
        message: `Retrying request (attempt ${attempt + 1}/${mergedConfig.maxRetries}): ${lastError.message}`,
        action: 'api-retry',
        metadata: {
          attempt: attempt + 1,
          delay,
          maxRetries: mergedConfig.maxRetries,
        },
      });

      // Notify callback if provided
      if (mergedConfig.onRetry) {
        mergedConfig.onRetry(attempt + 1, lastError, delay);
      }

      await sleep(delay);
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError || new Error('Retry failed');
}

/**
 * Create a retryable fetch wrapper
 */
export function createRetryableFetch(config?: Partial<RetryConfig>) {
  return async function retryableFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    return withRetry(async () => {
      const response = await fetch(input, init);

      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`) as Error & {
          status: number;
        };
        (error as Error & { status: number }).status = response.status;
        throw error;
      }

      return response;
    }, config);
  };
}

/** Pre-configured retryable fetch with default settings */
export const retryableFetch = createRetryableFetch();
